import { useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { Link, useSearchParams } from "react-router";

import { getMessagingStatus, listMessagingEmails } from "@/api/messaging";
import { Field, Input, Select } from "@/components/form";
import { EventBadge } from "@/components/messaging/badges";
import { RelativeTime } from "@/components/RelativeTime";
import { ErrorBox, RefreshFailed, isLoadingOrPaused, loadFailure } from "@/components/Spinner";
import type { MessagingDeliveryEvents, MessagingEmail } from "@/types/api";
import { EmptyState, LoadingRegion, Page, PageHeader, Skeleton, Table, type TableColumn } from "@/components/ui";
import { formatDateTime } from "@/lib/dates";
import { KIND_LABELS, kindLabel } from "@/lib/messaging";
import { MESSAGING } from "@/lib/sections";

const DAY_MS = 24 * 60 * 60 * 1000;

const EVENT_FILTERS: { label: string; value: string }[] = [
  { label: "Any event", value: "" },
  { label: "Sent", value: "sent" },
  { label: "Delivered", value: "delivered" },
  { label: "Delayed", value: "delivery_delayed" },
  { label: "Bounced", value: "bounced" },
  { label: "Complained", value: "complained" },
  { label: "Failed", value: "failed" },
];

const KIND_FILTERS: { label: string; value: string }[] = [
  { label: "Any kind", value: "" },
  ...Object.entries(KIND_LABELS).map(([value, label]) => ({ label, value })),
];

const SOURCE_LABELS: Record<MessagingDeliveryEvents["source"], string> = {
  transactional: "Transactional",
  waitlist: "Waitlist",
};

function StatusStrip({ token }: { token: string }) {
  const status = useQuery({
    queryKey: ["messaging", "status"],
    queryFn: () => getMessagingStatus(token),
    enabled: !!token,
    refetchInterval: 60_000,
  });
  if (isLoadingOrPaused(status)) {
    return (
      <LoadingRegion label="Loading delivery status…">
        <div className="card mb-4">
          <Skeleton className="h-5 w-2/3" />
        </div>
      </LoadingRegion>
    );
  }
  // A failed poll keeps the last figures on screen, flagged as stale.
  if (status.isLoadingError) return <ErrorBox error={status.error} />;
  if (!status.data) return null;
  const { deliveryEvents, last24h } = status.data;
  return (
    <>
      <RefreshFailed query={status} className="mb-4" />
      <div className="card mb-4 flex flex-wrap items-center gap-x-8 gap-y-2 text-sm">
        <div className="flex flex-wrap gap-x-6 gap-y-1">
          <span className="font-medium text-ink">Delivery events:</span>
          {deliveryEvents.map((events) => {
            // Red only when something was sent through that source and nothing came back within 24h — the
            // same condition as the silent-source alarms. A source that sent nothing has no basis to complain.
            const stale =
              events.sentLast24h > 0 && (!events.lastReceivedAt || Date.now() - events.lastReceivedAt > DAY_MS);
            return (
              <span key={events.source} data-source={events.source} className={stale ? "text-danger-fg" : "text-ink"}>
                <span className="font-medium">{SOURCE_LABELS[events.source]}:</span>{" "}
                {events.lastReceivedAt ? (
                  <>
                    last received <RelativeTime epochMs={events.lastReceivedAt} />
                    {events.lastEventType ? ` (${events.lastEventType})` : ""}
                  </>
                ) : (
                  "never received"
                )}
                {stale && " — check the delivery events"}
              </span>
            );
          })}
        </div>
        <div className="flex gap-4 text-ink-muted">
          <span>Last 24h:</span>
          <span>
            <strong>{last24h.sent}</strong> sent
          </span>
          <span>
            <strong>{last24h.delivered}</strong> delivered
          </span>
          <span className={last24h.bounced ? "text-danger-fg" : ""}>
            <strong>{last24h.bounced}</strong> bounced
          </span>
          <span className={last24h.complained ? "text-danger-fg" : ""}>
            <strong>{last24h.complained}</strong> complained
          </span>
          {last24h.truncated && <span className="text-ink-muted">(first 5000 only)</span>}
        </div>
      </div>
    </>
  );
}

const COLUMNS: TableColumn<MessagingEmail>[] = [
  { key: "sent", header: "Sent", mono: true, nowrap: true, cell: (email) => formatDateTime(email.sentAt) },
  { key: "to", header: "To", cell: (email) => email.to.join(", ") },
  {
    key: "subject",
    header: "Subject",
    cell: (email) => (
      <Link to={`/messaging/emails/${encodeURIComponent(email.emailId)}`} className="text-brand-700 hover:underline">
        {email.subject || "(no subject)"}
      </Link>
    ),
  },
  { key: "kind", header: "Kind", cell: (email) => kindLabel(email.kind) },
  {
    key: "tenant",
    header: "Tenant",
    cell: (email) => (
      <>
        {email.tenantId ? (
          <Link to={`/tenants/${email.tenantId}`} className="hover:underline">
            {email.tenantId}
          </Link>
        ) : (
          <span className="text-ink-muted">—</span>
        )}
        {email.stage ? <span className="text-ink-muted"> / {email.stage}</span> : null}
      </>
    ),
  },
  { key: "event", header: "Last event", cell: (email) => <EventBadge event={email.lastEvent} /> },
];

export function MessagingEmailsPage() {
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";
  const [searchParams, setSearchParams] = useSearchParams();
  const tenantId = searchParams.get("tenant") ?? "";
  const [kind, setKind] = useState("");
  const [lastEvent, setLastEvent] = useState("");

  const emails = useInfiniteQuery({
    queryKey: ["messaging", "emails", { tenantId, kind, lastEvent }],
    queryFn: ({ pageParam }) =>
      listMessagingEmails(token, { tenantId, kind, lastEvent, cursor: pageParam, limit: 50 }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: !!token,
  });

  const items: MessagingEmail[] | undefined = emails.data?.pages.flatMap((p) => p.items);

  return (
    <Page>
      <PageHeader
        section={MESSAGING}
        title="Emails"
        subtitle="Every email the control plane sends — account invitations, password resets, security codes — and what happened to it after it left."
      />

      <StatusStrip token={token} />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label="Tenant">
          <Input
            placeholder="tenant id"
            value={tenantId}
            onChange={(e) => {
              const next = new URLSearchParams(searchParams);
              if (e.target.value) next.set("tenant", e.target.value);
              else next.delete("tenant");
              setSearchParams(next, { replace: true });
            }}
          />
        </Field>
        <Field label="Kind">
          <Select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            options={KIND_FILTERS}
          />
        </Field>
        <Field label="Last event">
          <Select
            value={lastEvent}
            onChange={(e) => setLastEvent(e.target.value)}
            options={EVENT_FILTERS}
          />
        </Field>
      </div>

      <RefreshFailed query={emails} className="mb-4" />
      <Table
        label="Emails"
        columns={COLUMNS}
        rows={items}
        rowKey={(email) => email.emailId}
        error={loadFailure(emails)}
        more={
          emails.hasNextPage
            ? { loading: emails.isFetchingNextPage, onLoadMore: () => void emails.fetchNextPage() }
            : undefined
        }
        empty={
          tenantId || kind || lastEvent ? (
            <EmptyState title="No emails match these filters" />
          ) : (
            <EmptyState title="No emails yet">Every email the control plane sends will be listed here.</EmptyState>
          )
        }
      />
    </Page>
  );
}
